import { z } from 'zod';
import type { EnvType } from '../alive/aliveResponse';
import { LANGUAGES, type Language } from '../task/task';

/**
 * How many days a stored object — an extraction, or a job's study material, which is the PDF's
 * text and never the PDF itself — is kept before it expires (QZ-D71, QZ-D80).
 */
export const MATERIAL_RETENTION_DAYS = 90;

/** The study material bucket's name for an environment; `local` shares dev's bucket (PDG-D8). */
export const materialBucketName = (envType: EnvType): string =>
  `quizdb-${envType === 'local' ? 'dev' : envType}-material`;

/** The most bytes an extraction upload may be; the API reads it whole in one Lambda (PTX-D3). */
export const EXTRACTION_MAX_BYTES = 25 * 1024 * 1024;

/** The most characters a job's page range's text may hold (PTX-D3). */
export const MATERIAL_MAX_CHARS = 1_500_000;

/** The most photos a photo set may hold (PHO-D5). */
export const MAX_PHOTOS = 100;

/** One PDF page's kept text; `text` may be empty (PTX-D1). */
export const pageTextSchema = z
  .object({ page: z.number().int().min(1), text: z.string() })
  .strict();
export type PageText = z.infer<typeof pageTextSchema>;

/**
 * A PDF's extracted text: either a whole extraction, holding every page from 1 to `pdfPageCount`,
 * or a job's material, holding exactly its page range (PTX-D1). `pages` must be strictly
 * ascending by `page`, and no `page` may exceed `pdfPageCount`.
 */
export const studyMaterialTextSchema = z
  .object({
    pdfPageCount: z.number().int().min(1),
    pages: z.array(pageTextSchema),
  })
  .strict()
  .superRefine((value, ctx) => {
    value.pages.forEach((page, i) => {
      if (i > 0 && page.page <= value.pages[i - 1].page) {
        ctx.addIssue({
          code: 'custom',
          path: ['pages', i, 'page'],
          message: 'A page must be strictly greater than the one before it.',
        });
      }
      if (page.page > value.pdfPageCount) {
        ctx.addIssue({
          code: 'custom',
          path: ['pages', i, 'page'],
          message: 'A page must not exceed pdfPageCount.',
        });
      }
    });
  });
export type StudyMaterialText = z.infer<typeof studyMaterialTextSchema>;

/** A job's page range into a PDF, 1-based and inclusive. */
export const pageRangeSchema = z
  .object({
    from: z.number().int().min(1),
    to: z.number().int().min(1),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.from > value.to) {
      ctx.addIssue({ code: 'custom', path: ['to'], message: 'to must not be before from.' });
    }
  });
export type PageRange = z.infer<typeof pageRangeSchema>;

/** The alternation of `LANGUAGES`, used to build the extraction id patterns below (PHO-D6). */
const LANGUAGE_PATTERN = LANGUAGES.join('|');

/**
 * An extraction's id, in one of three forms (PTX-D2, QZ-D83, PHO-D6): `<sha>-v<n>`, a PDF read
 * wholly from its text layer; `<sha>-v<n>-ocr<m>-<lang>`, a PDF with any page read by OCR; or
 * `<sha>-ocr<m>-<lang>`, a photo set. `<sha>` is 64 lower-case hex, `n` and `m` are positive
 * integers with no leading zero, and `<lang>` is one of `LANGUAGES`.
 */
export const extractionIdSchema = z
  .string()
  .regex(
    new RegExp(
      `^[0-9a-f]{64}(?:-v[1-9][0-9]*(?:-ocr[1-9][0-9]*-(?:${LANGUAGE_PATTERN}))?|-ocr[1-9][0-9]*-(?:${LANGUAGE_PATTERN}))$`
    )
  );

/** A photo set's extraction id pattern alone, used by `isPhotoExtractionId` (PHO-D6). */
const PHOTO_EXTRACTION_ID_PATTERN = new RegExp(
  `^[0-9a-f]{64}-ocr[1-9][0-9]*-(?:${LANGUAGE_PATTERN})$`
);

/** The id for a PDF's extraction at the given extractor version (PTX-D2, QZ-D83). */
export const extractionIdFor = (sha256: string, version: number): string => `${sha256}-v${version}`;

/**
 * The id for a PDF's extraction once any page was read by OCR, at the given extractor and OCR
 * versions and language (PHO-D6).
 */
export const ocrExtractionIdFor = (
  sha256: string,
  extractorVersion: number,
  ocrVersion: number,
  language: Language
): string => `${sha256}-v${extractorVersion}-ocr${ocrVersion}-${language}`;

/** The id for a photo set's extraction at the given OCR version and language (PHO-D6). */
export const photoExtractionIdFor = (
  setSha256: string,
  ocrVersion: number,
  language: Language
): string => `${setSha256}-ocr${ocrVersion}-${language}`;

/** Whether `id` is a well-formed photo set extraction id, the third of the three forms (PHO-D6). */
export const isPhotoExtractionId = (id: string): boolean => PHOTO_EXTRACTION_ID_PATTERN.test(id);

/** The object key for the extraction with the given id (PTX-D2). */
export const extractionKey = (id: string): string => `extraction/${id}.json`;

/**
 * A job's own study-material object key: `material/<uuid>.json`, lower-case as `randomUUID`
 * mints, minted by the API (PTX-D2).
 */
export const textMaterialKeySchema = z
  .string()
  .regex(/^material\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.json$/);

/** The object key for a job's study material minted with id `id` (PTX-D2). */
export const newTextMaterialKey = (id: string): string => `material/${id}.json`;

/** The total characters kept across `pages` (PTX-D3). */
export const countTextChars = (pages: readonly PageText[]): number =>
  pages.reduce((total, page) => total + page.text.length, 0);

/** Each PDF page's kept character count, indexed `page - 1`; 0 for a page absent from `pages`. */
export const pageCharCounts = (text: StudyMaterialText): number[] => {
  const counts = new Array(text.pdfPageCount).fill(0);
  for (const page of text.pages) {
    counts[page.page - 1] = page.text.length;
  }
  return counts;
};

/** Whether `text` holds every page from 1 to `pdfPageCount`, as a whole extraction must (PTX-D14). */
export const isCompleteExtraction = (text: StudyMaterialText): boolean =>
  text.pages.length === text.pdfPageCount && text.pages.every((page, i) => page.page === i + 1);

/**
 * `text` narrowed to the pages of `range`, keeping `pdfPageCount`. Throws if `range.to` exceeds
 * `pdfPageCount`.
 */
export const selectPageRange = (text: StudyMaterialText, range: PageRange): StudyMaterialText => {
  if (range.to > text.pdfPageCount) {
    throw new Error("A page range's to must not exceed pdfPageCount.");
  }
  return {
    pdfPageCount: text.pdfPageCount,
    pages: text.pages.filter((page) => page.page >= range.from && page.page <= range.to),
  };
};
